import { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useChatStore, type Agent, type AgentMetadata } from '../stores/chatStore'
import { getAgent, getAgentFeedback, getAgentCapabilities, formatRelativeTime, fetchMCPTools, fetchA2AAgentCard, type FeedbackItem, type FeedbackRating, type MCPTool, type DBCapability } from '../lib/api'
import { ToolTester } from '../components/ToolTester'
import { A2AMessenger } from '../components/A2AMessenger'

// Chain ID to name mapping
const CHAIN_NAMES: Record<number, string> = {
  1: 'Ethereum',
  11155111: 'Sepolia',
  8453: 'Base',
  84532: 'Base Sepolia',
  137: 'Polygon',
  80002: 'Polygon Amoy',
}

// Block explorer URLs
const BLOCK_EXPLORERS: Record<number, string> = {
  1: 'https://etherscan.io',
  11155111: 'https://sepolia.etherscan.io',
  8453: 'https://basescan.org',
  84532: 'https://sepolia.basescan.org',
  137: 'https://polygonscan.com',
  80002: 'https://amoy.polygonscan.com',
}

interface Capability {
  name: string
  type: 'skill' | 'domain' | 'tool'
  protocol?: string
  source: string  // e.g., "MCP endpoint (live)", "A2A.a2aSkills"
  sources?: string[]  // All sources if merged from multiple
  description?: string
  endpointUrl?: string  // The endpoint where capability was discovered
  endpointsByProtocol?: Record<string, string>  // Maps protocol → endpoint URL (preserved across merges)
  messagingUrl?: string  // For A2A: the actual URL to send messages to (from agent_card.url)
  messagingUrlWarning?: string  // Warning if URL looks like a website, not a service endpoint
  requiresPayment?: boolean  // For A2A: true if endpoint returns 402 (x402)
  preferredTransport?: string  // For A2A: how to call the endpoint (jsonrpc, rest, grpc)
  examples?: string[]  // Example prompts/messages for the skill
  isProbed?: boolean  // Whether this capability was discovered via live probing
  rawJson?: Record<string, unknown>  // The original JSON object from the endpoint
  allRawJson?: Array<{ source: string; data: Record<string, unknown> }>  // All raw JSON if merged
}

// Normalize capability name for deduplication (e.g., "Fetch API" and "fetch_api" become "fetchapi")
function normalizeCapabilityName(name: string | undefined): string {
  if (!name) return ''
  return name
    .toLowerCase()
    .replace(/[_\-\s]+/g, '')  // Remove underscores, hyphens, spaces
    .replace(/[^a-z0-9]/g, '') // Remove other special characters
}

// Convert snake_case/kebab-case to Title Case for display (e.g., "fetch_page" -> "Fetch Page")
function toDisplayName(name: string | undefined): string {
  if (!name) return ''
  // If already has spaces and capitals, it's probably already formatted
  if (/[A-Z]/.test(name) && name.includes(' ')) {
    return name
  }

  return name
    .replace(/[_\-]+/g, ' ')  // Replace underscores/hyphens with spaces
    .replace(/([a-z])([A-Z])/g, '$1 $2')  // Add space before capitals (camelCase)
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

// Score for choosing best display name - prefer A2A over OASF, then human-readable
function getDisplayNameScore(name: string | undefined, protocol?: string): number {
  if (!name) return 0
  let score = 0
  // Protocol preference: A2A names take priority over OASF
  if (protocol === 'a2a') score += 100
  if (protocol === 'mcp') score += 50
  // Human-readable formatting
  if (name.includes(' ')) score += 10  // Has spaces (more readable)
  if (/[A-Z]/.test(name)) score += 5   // Has capitals
  if (!name.includes('_')) score += 2  // No underscores
  return score
}

// Merge capabilities by name across protocols, preserving per-protocol endpoint URLs
function mergeCapabilities(capabilities: Capability[]): Capability[] {
  const merged = new Map<string, Capability>()

  for (const cap of capabilities) {
    const normalizedName = normalizeCapabilityName(cap.name)
    const existing = merged.get(normalizedName)

    if (!existing) {
      // Initialize with sources array, allRawJson, and endpointsByProtocol
      const allRawJson = cap.rawJson ? [{ source: cap.source, data: cap.rawJson }] : []
      const endpointsByProtocol: Record<string, string> = {}
      if (cap.protocol && cap.endpointUrl) endpointsByProtocol[cap.protocol] = cap.endpointUrl
      merged.set(normalizedName, { ...cap, sources: [cap.source], allRawJson, endpointsByProtocol })
    } else {
      // Collect all unique sources
      const allSources = existing.sources || [existing.source]
      if (!allSources.includes(cap.source)) {
        allSources.push(cap.source)
      }

      // Collect all rawJson objects
      const allRawJson = existing.allRawJson || (existing.rawJson ? [{ source: existing.source, data: existing.rawJson }] : [])
      if (cap.rawJson && !allRawJson.some(r => r.source === cap.source)) {
        allRawJson.push({ source: cap.source, data: cap.rawJson })
      }

      // Preserve per-protocol endpoint URLs
      const endpointsByProtocol = { ...existing.endpointsByProtocol }
      if (cap.protocol && cap.endpointUrl) endpointsByProtocol[cap.protocol] = cap.endpointUrl

      // Preserve messagingUrl, messagingUrlWarning, and preferredTransport from either capability
      const messagingUrl = cap.messagingUrl || existing.messagingUrl
      const messagingUrlWarning = cap.messagingUrlWarning || existing.messagingUrlWarning
      const preferredTransport = cap.preferredTransport || existing.preferredTransport

      // Prefer: live > metadata, tool > skill > domain, has rawJson > no rawJson
      const existingScore = getCapabilityScore(existing)
      const newScore = getCapabilityScore(cap)

      // Pick the best display name (prefer A2A > MCP > OASF, then human-readable)
      const bestDisplayName = getDisplayNameScore(cap.name, cap.protocol) > getDisplayNameScore(existing.name, existing.protocol)
        ? cap.name
        : existing.name

      if (newScore > existingScore) {
        merged.set(normalizedName, { ...cap, name: bestDisplayName, sources: allSources, allRawJson, endpointsByProtocol, messagingUrl, messagingUrlWarning, preferredTransport })
      } else {
        existing.sources = allSources
        existing.allRawJson = allRawJson
        existing.endpointsByProtocol = endpointsByProtocol
        existing.messagingUrl = messagingUrl
        existing.messagingUrlWarning = messagingUrlWarning
        existing.preferredTransport = preferredTransport
        if (getDisplayNameScore(cap.name, cap.protocol) > getDisplayNameScore(existing.name, existing.protocol)) {
          existing.name = cap.name
        }
      }
    }
  }

  return Array.from(merged.values())
}

function getCapabilityScore(cap: Capability): number {
  let score = 0
  if (cap.isProbed) score += 100
  if (cap.type === 'tool') score += 10
  else if (cap.type === 'skill') score += 5
  if (cap.rawJson) score += 2
  if (cap.description) score += 1
  return score
}

// Extract capabilities from metadata (fallback when no live data)
// Note: OASF skills/domains are taxonomy-only and hidden from display
function extractMetadataCapabilities(_agent: Agent): Capability[] {
  return []
}

export default function AgentDetailPage() {
  const { agentKey } = useParams<{ agentKey: string }>()
  const navigate = useNavigate()
  const goToWelcome = useChatStore((s) => s.goToWelcome)
  const hasSearchResults = useChatStore((s) => s.messages.length > 0)
  const [searchParams, setSearchParams] = useSearchParams()
  const toolParam = searchParams.get('tool')
  const skillParam = searchParams.get('skill')
  const [agent, setAgent] = useState<Agent | null>(null)
  const [feedback, setFeedback] = useState<FeedbackItem[]>([])
  const [feedbackRating, setFeedbackRating] = useState<FeedbackRating | null>(null)
  const [loadingFeedback, setLoadingFeedback] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'reviews'>('overview')
  const [showAllReviews, setShowAllReviews] = useState(false)
  const [showAllTags, setShowAllTags] = useState(false)
  const [selectedTag, setSelectedTag] = useState<string | null>(null)
  const TAGS_LIMIT = 8
  const [testingEndpoint, setTestingEndpoint] = useState<{ url: string; protocol: string; toolName?: string } | null>(null)
  const [a2aMessenger, setA2aMessenger] = useState<{ url: string; skillName?: string; transport?: string; warning?: string; examples?: string[]; requiresPayment?: boolean; paymentInfo?: Record<string, unknown> } | null>(null)
  const [liveCapabilities, setLiveCapabilities] = useState<Capability[]>([])
  const [loadingCapabilities, setLoadingCapabilities] = useState(false)
  const [a2aEndpointInfo, setA2aEndpointInfo] = useState<Record<string, { messagingUrl?: string; warning?: string; transport?: string; requiresPayment?: boolean; paymentInfo?: Record<string, unknown> }>>({})
  const [mcpEndpointInfo, setMcpEndpointInfo] = useState<Record<string, { loaded: boolean; toolCount: number }>>({})
  const [selectedCapability, setSelectedCapability] = useState<Capability | null>(null)
  const [showAllCapabilities, setShowAllCapabilities] = useState(false)
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem('theme') as 'dark' | 'light') || 'dark'
    }
    return 'dark'
  })

  useEffect(() => {
    const root = document.documentElement
    if (theme === 'dark') {
      root.classList.add('dark')
      root.classList.remove('light')
    } else {
      root.classList.add('light')
      root.classList.remove('dark')
    }
    localStorage.setItem('theme', theme)
  }, [theme])

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark')
  }

  useEffect(() => {
    if (!agentKey) return

    setLoading(true)
    setError(null)

    getAgent(decodeURIComponent(agentKey))
      .then(setAgent)
      .catch((err) => setError(err.message || 'Failed to load agent'))
      .finally(() => setLoading(false))
  }, [agentKey])

  // Fetch feedback when agent loads
  useEffect(() => {
    if (!agentKey || !agent) return

    setLoadingFeedback(true)
    getAgentFeedback(decodeURIComponent(agentKey))
      .then((res) => {
        setFeedback(res.feedback.filter(f => !f.is_revoked))
        setFeedbackRating(res.rating)
      })
      .catch(() => {
        setFeedback([])
        setFeedbackRating(null)
      })
      .finally(() => setLoadingFeedback(false))
  }, [agentKey, agent])

  // Fetch capabilities from DB (includes payment info from background probing)
  useEffect(() => {
    if (!agentKey) return

    setLoadingCapabilities(true)

    getAgentCapabilities(agentKey)
      .then((res) => {
        // Convert DB capabilities to the Capability format used by the UI
        // Filter out OASF skills/domains (taxonomy only, not actionable)
        const caps: Capability[] = res.capabilities.filter((dbCap: DBCapability) =>
          dbCap.type !== 'oasf_skill' && dbCap.type !== 'oasf_domain'
        ).map((dbCap: DBCapability): Capability => {
          // Determine protocol and type from capability_type
          let protocol: string | undefined
          let type: 'tool' | 'skill' | 'domain' = 'skill'

          if (dbCap.type === 'mcp_tool') {
            protocol = 'mcp'
            type = 'tool'
          } else if (dbCap.type === 'a2a_skill') {
            protocol = 'a2a'
            type = 'skill'
          } else if (dbCap.type === 'oasf_skill') {
            protocol = 'oasf'
            type = 'skill'
          } else if (dbCap.type === 'oasf_domain') {
            protocol = 'oasf'
            type = 'domain'
          }

          return {
            name: dbCap.name,
            type,
            protocol,
            source: protocol?.toUpperCase() || 'unknown',
            isProbed: dbCap.source === 'probed',
            description: dbCap.description || undefined,
            endpointUrl: dbCap.endpoint_url || undefined,
            requiresPayment: dbCap.requires_payment ?? undefined,
            rawJson: dbCap.raw_data || undefined,
          }
        })
        setLiveCapabilities(caps)
      })
      .catch(() => {
        // Fallback: capabilities will come from metadata extraction only
        setLiveCapabilities([])
      })
      .finally(() => setLoadingCapabilities(false))
  }, [agentKey])

  // Probe live endpoints for service section info (tool counts, messaging URLs)
  // This is separate from capability display to keep the UI responsive
  useEffect(() => {
    if (!agent?.metadata?.services) return

    const mcpServices = agent.metadata.services.filter(
      s => s.name?.toUpperCase() === 'MCP' && s.endpoint
    )
    const a2aServices = agent.metadata.services.filter(
      s => s.name?.toUpperCase() === 'A2A' && s.endpoint
    )

    // Probe MCP endpoints for tool counts
    mcpServices.forEach(async (service) => {
      try {
        const res = await fetchMCPTools(service.endpoint!)
        const toolCount = res.success ? res.tools.filter((t: MCPTool) => t.name && t.name.trim()).length : 0
        setMcpEndpointInfo(prev => ({
          ...prev,
          [service.endpoint!]: { loaded: true, toolCount }
        }))
      } catch {
        setMcpEndpointInfo(prev => ({
          ...prev,
          [service.endpoint!]: { loaded: true, toolCount: 0 }
        }))
      }
    })

    // Probe A2A endpoints for messaging info
    a2aServices.forEach(async (service) => {
      try {
        const res = await fetchA2AAgentCard(service.endpoint!)
        setA2aEndpointInfo(prev => ({
          ...prev,
          [service.endpoint!]: {
            messagingUrl: res.messaging_url,
            warning: res.messaging_url_warning,
            transport: res.preferred_transport,
            requiresPayment: res.requires_payment,
            paymentInfo: res.payment_info,
          }
        }))
      } catch {
        // Silent fail - info will be empty
      }
    })
  }, [agent])

  // Open tool tester if 'tool' query param is present
  useEffect(() => {
    if (!toolParam || !agent) return

    // Find MCP endpoint
    const mcpEndpoint = agent.endpoints.find(ep => ep.protocol_type === 'mcp')
    if (mcpEndpoint) {
      setTestingEndpoint({
        url: mcpEndpoint.base_url,
        protocol: 'mcp',
        toolName: toolParam,
      })
      // Clear the query param after opening
      setSearchParams({}, { replace: true })
    }
  }, [toolParam, agent, setSearchParams])

  // Open A2A messenger if 'skill' query param is present
  useEffect(() => {
    if (!skillParam || !agent) return

    // Find A2A endpoint with messaging info loaded
    const a2aEndpoint = agent.endpoints.find(ep => ep.protocol_type === 'a2a')
    if (!a2aEndpoint) return

    const info = a2aEndpointInfo[a2aEndpoint.base_url]
    if (!info?.messagingUrl) return  // Wait for async probe to finish

    setA2aMessenger({
      url: info.messagingUrl,
      skillName: skillParam,
      transport: info.transport,
      warning: info.warning,
      requiresPayment: info.requiresPayment,
      paymentInfo: info.paymentInfo,
    })
    setSearchParams({}, { replace: true })
  }, [skillParam, agent, a2aEndpointInfo, setSearchParams])

  if (loading) {
    return (
      <div className="min-h-screen bg-terminal flex items-center justify-center">
        <div className="text-[var(--text-muted)] font-mono">Loading agent...</div>
      </div>
    )
  }

  if (error || !agent) {
    return (
      <div className="min-h-screen bg-terminal flex flex-col items-center justify-center gap-4">
        <div className="text-[var(--text-muted)] font-mono">{error || 'Agent not found'}</div>
        <button
          onClick={() => { goToWelcome(); navigate('/'); }}
          className="px-4 py-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-default)] hover:border-[var(--accent-teal)] transition-colors font-mono text-sm"
        >
          Back to Search
        </button>
      </div>
    )
  }

  // Agent status computed from probing data
  const isCallable = agent.availability === 'callable'
  const isReachable = agent.availability === 'reachable'
  const statusLabel = isCallable ? '✅ Callable' : isReachable ? '🟡 Reachable' : '🧪 Experimental'
  const statusTooltip = isCallable ? 'MCP/A2A services are callable' : isReachable ? 'Listed MCP or A2A services but not yet callable' : 'No MCP or A2A services listed'

  // Domain verification - use cached value from DB (updated by daily refresh script)
  const isAgentVerified = agent.domain_verified === true
  const verificationMessage = agent.verification_reason === 'same_domain'
    ? 'Endpoint domain matches agent metadata URI'
    : agent.verification_reason === 'registration_match'
    ? 'Verified via agent-registration.json'
    : agent.domain_verified === true
    ? 'Domain verified'
    : agent.domain_verified === false
    ? 'Domain not verified'
    : 'Not yet checked'

  const chainName = CHAIN_NAMES[agent.chain_id] || `Chain ${agent.chain_id}`
  const explorerUrl = BLOCK_EXPLORERS[agent.chain_id]
  const nftUrl = explorerUrl ? `${explorerUrl}/nft/${agent.registry_address}/${agent.token_id}` : null

  // Use deduplicated rating if available, otherwise fall back to agent data
  const avgScore = feedbackRating?.score ?? agent.reputation_score ?? 0
  const totalFeedback = feedbackRating?.total_feedback ?? agent.reputation_count ?? 0

  // Merge live capabilities (from MCP endpoints) with metadata capabilities (OASF skills/domains)
  // Deduplicate by normalized name, preferring live data and richer types
  const metadataCaps = extractMetadataCapabilities(agent)
  const capabilities = mergeCapabilities([...liveCapabilities, ...metadataCaps])
    .sort((a, b) => {
      // Payment status order: free (0) > paid (1) > unknown (2)
      const getPaymentOrder = (cap: Capability) => {
        if (cap.requiresPayment === false) return 0  // FREE first
        if (cap.requiresPayment === true) return 1   // $ second
        return 2  // unknown last
      }
      const aPayment = getPaymentOrder(a)
      const bPayment = getPaymentOrder(b)
      if (aPayment !== bPayment) return aPayment - bPayment
      // Then sort by type: tools > skills > domains
      const typeOrder = { tool: 0, skill: 1, domain: 2 }
      const aOrder = typeOrder[a.type] ?? 3
      const bOrder = typeOrder[b.type] ?? 3
      if (aOrder !== bOrder) return aOrder - bOrder
      // Finally alphabetical
      return (a.name || '').localeCompare(b.name || '')
    })

  // Extract unique tags from feedback for filtering (starred first, then alphabetical)
  const uniqueTags = Array.from(new Set(
    feedback.flatMap(f => [f.tag1, f.tag2].filter(Boolean) as string[])
  )).sort((a, b) => {
    if (a === 'starred') return -1
    if (b === 'starred') return 1
    return a.localeCompare(b)
  })

  // Filter feedback by selected tag
  const filteredFeedback = selectedTag
    ? feedback.filter(f => f.tag1 === selectedTag || f.tag2 === selectedTag)
    : feedback

  return (
    <div className="min-h-screen bg-terminal">
      {/* Header Bar */}
      <div className="sticky top-0 z-10 bg-terminal/95 backdrop-blur-sm border-b border-[var(--border-subtle)]">
        <div className="max-w-2xl lg:max-w-6xl mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => { goToWelcome(); navigate('/'); }}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <div className="w-7 h-7 rounded bg-[var(--accent-teal)] flex items-center justify-center">
                <span className="font-mono font-bold text-xs text-[var(--bg-primary)]">8K</span>
              </div>
              <span className="font-display font-semibold text-sm">8kconnect</span>
            </button>
            {hasSearchResults && (
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-1 text-xs font-mono text-[var(--text-muted)] hover:text-[var(--accent-teal)] transition-colors"
              >
                <span>&larr;</span>
                <span>Back to results</span>
              </button>
            )}
          </div>
          <button
            onClick={toggleTheme}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors text-sm font-mono text-[var(--text-muted)]"
          >
            {theme === 'dark' ? (
              <>
                <SunIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Light</span>
              </>
            ) : (
              <>
                <MoonIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Dark</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* ========== DESKTOP LAYOUT (lg+) ========== */}
      <div className="hidden lg:block">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="grid grid-cols-3 gap-6">
            {/* Left Column - Agent Info */}
            <div className="col-span-2 space-y-6">
              {/* Agent Header */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[var(--bg-secondary)] rounded-2xl border border-[var(--border-default)] p-6 overflow-hidden"
              >
                <div className="flex gap-5 overflow-hidden">
                  {agent.image_url ? (
                    <img
                      src={agent.image_url}
                      alt=""
                      className="w-20 h-20 rounded-xl object-cover bg-[var(--bg-tertiary)] flex-shrink-0"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-[var(--accent-teal)]/20 to-[var(--accent-purple)]/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-3xl font-display font-bold text-[var(--accent-teal)]">
                        {(agent.name || '?')[0]?.toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <h1 className="font-display text-2xl font-semibold text-[var(--text-primary)] truncate">
                          {agent.name || `Agent #${agent.token_id}`}
                        </h1>
                        <div className="flex items-center gap-3 mt-2 flex-wrap">
                          <div className="flex items-center gap-2">
                            <span className="text-xl font-semibold text-[var(--text-primary)]">{avgScore.toFixed(1)}</span>
                            <Tooltip text="Score calculated using an internal algorithm">
                              <StarRating score={avgScore} />
                            </Tooltip>
                            <span className="text-sm text-[var(--text-muted)]">
                              ({totalFeedback} {totalFeedback === 1 ? 'review' : 'reviews'})
                            </span>
                          </div>
                          <span className="text-[var(--text-muted)]">·</span>
                          <span className="text-sm text-[var(--text-secondary)]">{chainName}</span>
                          <span className="text-[var(--text-muted)]">·</span>
                          <span className="text-sm font-mono text-[var(--text-muted)]">#{agent.token_id}</span>
                        </div>
                      </div>
                    </div>
                    {agent.description && (
                      <p className="text-sm text-[var(--text-secondary)] leading-relaxed mt-4">
                        <LinkifiedText text={agent.description} />
                      </p>
                    )}
                    {/* Attribute Badges - Below Description */}
                    <div className="flex items-center gap-2 flex-wrap mt-4">
                      <Tooltip text={statusTooltip}>
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-mono ${
                            isCallable
                              ? 'bg-[var(--status-online)]/15 text-[var(--status-online)]'
                              : isReachable
                              ? 'bg-[var(--accent-gold)]/15 text-[var(--accent-gold)]'
                              : 'bg-[var(--text-muted)]/15 text-[var(--text-muted)]'
                          }`}
                        >
                          {statusLabel}
                        </span>
                      </Tooltip>
                      {(agent.x402_support === 'true' ||
                        agent.metadata?.x402Support === true ||
                        (agent.metadata?.additional_fields as Record<string, unknown>)?.x402support === true ||
                        (agent.metadata?.additional_fields as Record<string, unknown>)?.x402Support === true) && (
                        <Tooltip text="Supports x402 payment protocol for paid API calls">
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-mono bg-[var(--accent-gold)]/15 text-[var(--accent-gold)]">
                            $ x402
                          </span>
                        </Tooltip>
                      )}
                      {/* Domain Verification Badge */}
                      {agent.domain_verified !== null && (
                        <Tooltip text={verificationMessage}>
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-mono ${
                            isAgentVerified
                              ? 'bg-[var(--status-online)]/15 text-[var(--status-online)]'
                              : 'bg-[var(--text-muted)]/15 text-[var(--text-muted)]'
                          }`}>
                            {isAgentVerified ? (
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            )}
                            {isAgentVerified ? 'Domain Verified' : 'Domain Unverified'}
                          </span>
                        </Tooltip>
                      )}
                      {/* Reputation Trust Badge */}
                      {(agent.metadata?.supportedTrust as string[] | undefined)?.includes('reputation') && (
                        <Tooltip text="Agent supports on-chain reputation feedback">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-mono bg-[var(--accent-teal)]/15 text-[var(--accent-teal)]">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            Reputation
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Capabilities Section - Interactive Cards */}
              {(capabilities.length > 0 || loadingCapabilities) && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-[var(--bg-secondary)] rounded-2xl border border-[var(--border-default)] overflow-hidden"
                >
                  <div className="px-6 py-4 border-b border-[var(--border-subtle)] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <SparklesIcon className="w-5 h-5 text-[var(--accent-purple)]" />
                      <h2 className="text-lg font-display font-medium text-[var(--text-primary)]">Capabilities</h2>
                      {loadingCapabilities ? (
                        <span className="text-sm text-[var(--text-muted)] flex items-center gap-1">
                          <LoadingDots />
                          <span>Fetching from endpoints...</span>
                        </span>
                      ) : (
                        <span className="text-sm text-[var(--text-muted)]">({capabilities.length})</span>
                      )}
                    </div>
                  </div>
                  <div className="p-4 grid grid-cols-2 gap-3">
                    {(showAllCapabilities ? capabilities : capabilities.slice(0, 6)).map((cap, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05 * i }}
                        onClick={() => setSelectedCapability(cap)}
                        className={`p-4 rounded-xl border cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg ${
                          cap.type === 'skill'
                            ? 'bg-[var(--accent-purple)]/5 border-[var(--accent-purple)]/20 hover:border-[var(--accent-purple)]/40'
                            : cap.type === 'domain'
                            ? 'bg-[var(--accent-teal)]/5 border-[var(--accent-teal)]/20 hover:border-[var(--accent-teal)]/40'
                            : 'bg-[var(--accent-gold)]/5 border-[var(--accent-gold)]/20 hover:border-[var(--accent-gold)]/40'
                        }`}
                      >
                        <div className="flex flex-col gap-1.5 min-w-0">
                          <span
                            className={`text-sm font-mono font-medium ${
                              cap.type === 'skill' ? 'text-[var(--accent-purple)]'
                              : cap.type === 'domain' ? 'text-[var(--accent-teal)]'
                              : 'text-[var(--accent-gold)]'
                            }`}
                            style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                          >
                            {/* Don't normalize OASF skills/domains - show as-is */}
                            {cap.protocol === 'oasf' ? cap.name : toDisplayName(cap.name)}
                          </span>
                          {cap.description && (
                            <p className="text-xs text-[var(--text-muted)] line-clamp-2">
                              {cap.description}
                            </p>
                          )}
                          <div className="flex items-center gap-1 flex-wrap">
                            {/* Show all protocol sources if merged */}
                            {cap.sources && cap.sources.length > 1 ? (
                              <>
                                {Array.from(new Set(cap.sources.map(s => {
                                  if (s.includes('MCP')) return 'mcp'
                                  if (s.includes('A2A')) return 'a2a'
                                  if (s.includes('OASF') || s.includes('oasf')) return 'oasf'
                                  return null
                                }).filter(Boolean))).map((proto, i) => (
                                  <span key={i} className="px-1.5 py-0.5 text-[10px] font-mono uppercase rounded bg-[var(--bg-tertiary)] text-[var(--text-muted)]">
                                    {proto}
                                  </span>
                                ))}
                              </>
                            ) : cap.protocol && (
                              <span className="px-1.5 py-0.5 text-[10px] font-mono uppercase rounded bg-[var(--bg-tertiary)] text-[var(--text-muted)]">
                                {cap.protocol === 'http_unknown' ? 'http' : cap.protocol}
                              </span>
                            )}
                            {/* Show skill/domain for OASF */}
                            {cap.protocol === 'oasf' && (cap.type === 'skill' || cap.type === 'domain') && (
                              <span className={`px-1.5 py-0.5 text-[10px] font-mono rounded ${
                                cap.type === 'skill'
                                  ? 'bg-[var(--accent-purple)]/20 text-[var(--accent-purple)]'
                                  : 'bg-[var(--accent-teal)]/20 text-[var(--accent-teal)]'
                              }`}>
                                {cap.type}
                              </span>
                            )}
                            {cap.isProbed && (
                              <span className="px-1.5 py-0.5 text-[10px] font-mono rounded bg-[var(--status-online)]/20 text-[var(--status-online)]">
                                live
                              </span>
                            )}
                            {/* Show payment status indicator */}
                            {(() => {
                              const a2aUrl = cap.endpointsByProtocol?.a2a || cap.endpointUrl
                              const paymentStatus = cap.requiresPayment ?? (a2aUrl ? a2aEndpointInfo[a2aUrl]?.requiresPayment : undefined)
                              if (paymentStatus === true) {
                                return (
                                  <span className="px-1.5 py-0.5 text-[10px] font-mono font-bold rounded bg-amber-400/20 text-amber-400">
                                    $
                                  </span>
                                )
                              } else if (paymentStatus === false) {
                                return (
                                  <span className="px-1.5 py-0.5 text-[10px] font-mono rounded bg-emerald-400/20 text-emerald-400">
                                    FREE
                                  </span>
                                )
                              }
                              return null
                            })()}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  {capabilities.length > 6 && (
                    <div className="px-4 pb-4 pt-0">
                      <button
                        onClick={() => setShowAllCapabilities(!showAllCapabilities)}
                        className="w-full py-2 text-sm font-mono text-[var(--accent-teal)] hover:text-[var(--accent-teal)]/80 transition-colors"
                      >
                        {showAllCapabilities ? 'Show less' : `Show all ${capabilities.length} capabilities`}
                      </button>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Overview */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-[var(--bg-secondary)] rounded-2xl border border-[var(--border-default)] overflow-hidden"
              >
                <div className="px-6 py-4 border-b border-[var(--border-subtle)]">
                  <h2 className="text-lg font-display font-medium text-[var(--text-primary)]">Overview</h2>
                </div>
                {agent.metadata?.services && agent.metadata.services.length > 0 ? (
                  <div className="divide-y divide-[var(--border-subtle)]">
                    {[...agent.metadata.services].sort((a, b) => {
                      const priority: Record<string, number> = { 'A2A': 0, 'MCP': 1, 'OASF': 2 }
                      const nameA = a.name?.toUpperCase() || 'UNKNOWN'
                      const nameB = b.name?.toUpperCase() || 'UNKNOWN'
                      const priorityA = priority[nameA] ?? 100
                      const priorityB = priority[nameB] ?? 100
                      if (priorityA !== priorityB) return priorityA - priorityB
                      return nameA.localeCompare(nameB)
                    }).map((service, i) => {
                      const serviceName = service.name?.toUpperCase() || 'UNKNOWN';
                      const isMCP = serviceName === 'MCP';
                      const isA2A = serviceName === 'A2A';

                      // Get per-endpoint capability breakdown from DB capabilities
                      const endpointCaps = capabilities.filter(c =>
                        c.endpointUrl === service.endpoint &&
                        ((isMCP && c.protocol === 'mcp') || (isA2A && c.protocol === 'a2a'))
                      )
                      const probedCaps = endpointCaps.filter(c => c.isProbed)
                      const freeCaps = probedCaps.filter(c => c.requiresPayment === false)
                      const paidCaps = probedCaps.filter(c => c.requiresPayment === true)
                      const unknownCaps = probedCaps.filter(c => c.requiresPayment === undefined || c.requiresPayment === null)

                      // Get probe_status from endpoint data
                      const matchingEndpoint = agent.endpoints.find(ep => ep.base_url === service.endpoint)
                      const probeStatus = matchingEndpoint?.probe_status ?? null

                      // Determine per-endpoint probe status
                      const isEndpointCallable = probedCaps.length > 0 && (freeCaps.length > 0 || paidCaps.length > 0)
                      const isEndpointReachable = !isEndpointCallable && (probedCaps.length > 0 || probeStatus !== null)
                      const isEndpointUnprobed = !isEndpointCallable && !isEndpointReachable

                      return (
                        <div key={i} className="px-6 py-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`px-2 py-0.5 text-xs font-mono font-medium rounded uppercase ${
                                  isMCP ? 'bg-[var(--badge-mcp)]/20 text-[var(--badge-mcp)]'
                                  : isA2A ? 'bg-[var(--badge-a2a)]/20 text-[var(--badge-a2a)]'
                                  : 'bg-[var(--text-muted)]/20 text-[var(--text-muted)]'
                                }`}>
                                  {serviceName}
                                </span>
                                {/* Show verified checkmark for this specific endpoint */}
                                {service.endpoint && (agent.verified_endpoint === service.endpoint) && (
                                  <Tooltip text="This endpoint domain is verified">
                                    <span className="inline-flex items-center p-1 rounded bg-[var(--status-online)]/15 text-[var(--status-online)]">
                                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                      </svg>
                                    </span>
                                  </Tooltip>
                                )}
                              </div>
                              {service.endpoint && (
                                <a
                                  href={service.endpoint}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm font-mono text-[var(--accent-teal)] hover:underline break-all"
                                >
                                  {service.endpoint}
                                </a>
                              )}
                            </div>
                            <div className="flex gap-2 flex-shrink-0 ml-4">
                              {isMCP && service.endpoint && (() => {
                                const info = mcpEndpointInfo[service.endpoint!]
                                const hasTools = info?.toolCount && info.toolCount > 0
                                const tooltipMessage = !info?.loaded
                                  ? 'Loading tools...'
                                  : !hasTools
                                  ? 'No tools found at endpoint'
                                  : null

                                const button = (
                                  <button
                                    onClick={() => {
                                      if (hasTools) {
                                        setTestingEndpoint({ url: service.endpoint!, protocol: 'mcp' })
                                      }
                                    }}
                                    disabled={!hasTools}
                                    className={`px-4 py-2 rounded-lg border font-mono text-sm font-medium transition-colors ${
                                      hasTools
                                        ? 'border-[var(--accent-purple)] text-[var(--accent-purple)] hover:bg-[var(--accent-purple)]/10'
                                        : 'border-[var(--border-default)] text-[var(--text-muted)] cursor-not-allowed opacity-50'
                                    }`}
                                  >
                                    Try Tools
                                  </button>
                                )

                                return tooltipMessage ? (
                                  <Tooltip text={tooltipMessage}>{button}</Tooltip>
                                ) : button
                              })()}
                              {isA2A && service.endpoint && (() => {
                                const info = a2aEndpointInfo[service.endpoint!]
                                const hasMessagingUrl = info?.messagingUrl
                                const tooltipMessage = !info
                                  ? 'Loading agent card...'
                                  : !hasMessagingUrl
                                  ? 'No "url" field in A2A schema'
                                  : info.warning || null

                                const button = (
                                  <button
                                    onClick={() => {
                                      if (info?.messagingUrl) {
                                        setA2aMessenger({
                                          url: info.messagingUrl,
                                          transport: info.transport,
                                          warning: info.warning,
                                          requiresPayment: info.requiresPayment,
                                          paymentInfo: info.paymentInfo,
                                        })
                                      }
                                    }}
                                    disabled={!hasMessagingUrl}
                                    className={`px-4 py-2 rounded-lg border font-mono text-sm font-medium transition-colors flex items-center gap-1.5 ${
                                      hasMessagingUrl
                                        ? 'border-white/50 text-white hover:bg-white/10'
                                        : 'border-[var(--border-default)] text-[var(--text-muted)] cursor-not-allowed opacity-50'
                                    }`}
                                  >
                                    {info?.warning && (
                                      <svg className="w-3.5 h-3.5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                      </svg>
                                    )}
                                    {hasMessagingUrl && info?.requiresPayment === true && (
                                      <span className="text-amber-400 font-bold">$</span>
                                    )}
                                    {hasMessagingUrl && info?.requiresPayment === false && (
                                      <span className="text-emerald-400 text-[10px]">FREE</span>
                                    )}
                                    Send Message
                                  </button>
                                )

                                return tooltipMessage ? (
                                  <Tooltip text={tooltipMessage} position="bottom">
                                    {button}
                                  </Tooltip>
                                ) : button
                              })()}
                            </div>
                          </div>

                          {/* Endpoint probe status panel — only show if endpoint has been probed */}
                          {(isMCP || isA2A) && service.endpoint && !isEndpointUnprobed && (
                            <div className={`mt-3 px-3 py-2.5 rounded-lg border text-xs font-mono ${
                              isEndpointCallable
                                ? 'bg-[var(--status-online)]/8 border-[var(--status-online)]/20'
                                : isEndpointReachable
                                ? 'bg-[var(--accent-gold)]/8 border-[var(--accent-gold)]/20'
                                : 'bg-[var(--text-muted)]/8 border-[var(--text-muted)]/20'
                            }`}>
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isEndpointCallable ? 'status-pulse' : ''}`} style={{
                                  backgroundColor: isEndpointCallable ? 'var(--status-online)' : isEndpointReachable ? 'var(--accent-gold)' : 'var(--text-muted)'
                                }} />
                                <span className={`font-medium ${
                                  isEndpointCallable ? 'text-[var(--status-online)]' : isEndpointReachable ? 'text-[var(--accent-gold)]' : 'text-[var(--text-muted)]'
                                }`}>
                                  {isEndpointCallable ? 'Callable' : isEndpointReachable ? 'Reachable' : 'Not Yet Probed'}
                                </span>
                              </div>
                              <p className="text-[var(--text-muted)] leading-relaxed">
                                {isEndpointCallable && (
                                  <>
                                    {isMCP && (
                                      probeStatus ? (
                                        <>
                                          {probeStatus.replace(/^tools\/list:\s*/, '')}
                                        </>
                                      ) : (
                                        <>
                                          {`${probedCaps.length} tool${probedCaps.length !== 1 ? 's' : ''} discovered`}
                                          {freeCaps.length > 0 && <> &mdash; <span className="text-emerald-400">{freeCaps.length} free</span></>}
                                          {paidCaps.length > 0 && <> &mdash; <span className="text-amber-400">{paidCaps.length} paid</span></>}
                                          {unknownCaps.length > 0 && <> &mdash; {unknownCaps.length} unknown</>}
                                          .
                                        </>
                                      )
                                    )}
                                    {isA2A && (() => {
                                      const a2aInfo = a2aEndpointInfo[service.endpoint!]
                                      const endpointPaid = a2aInfo?.requiresPayment ?? (paidCaps.length > 0)
                                      const endpointFree = a2aInfo?.requiresPayment === false || (paidCaps.length === 0 && freeCaps.length > 0)
                                      const skillCount = probedCaps.length
                                      const statusText = probeStatus?.replace(/^Agent card:\s*/, '') || `${skillCount} skill${skillCount !== 1 ? 's' : ''} discovered`
                                      const pricingSummary = formatPaymentInfo(a2aInfo?.paymentInfo)
                                      return (
                                        <>
                                          {statusText}.{' '}
                                          {endpointPaid && <span className="text-amber-400">Payment required.</span>}
                                          {endpointFree && !endpointPaid && <span className="text-emerald-400">Free to use.</span>}
                                          {pricingSummary && (
                                            <span className="block mt-1 text-[var(--text-muted)]">{pricingSummary}</span>
                                          )}
                                        </>
                                      )
                                    })()}
                                  </>
                                )}
                                {isEndpointReachable && (
                                  <>
                                    {probeStatus || `Endpoint responded but payment status could not be determined for any ${isMCP ? 'tool' : 'skill'}.`}
                                  </>
                                )}
                                {isEndpointUnprobed && (
                                  <>
                                    {probeStatus || 'Not yet verified.'}
                                  </>
                                )}
                              </p>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="px-6 py-8">
                    <div className="text-center space-y-3">
                      <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[var(--bg-tertiary)]">
                        <svg className="w-5 h-5 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                        </svg>
                      </div>
                      <p className="text-sm font-mono text-[var(--text-muted)]">No metadata registered</p>
                      <p className="text-xs text-[var(--text-muted)]/70">This agent has no tokenURI set on-chain. Services and capabilities will appear here once metadata is published.</p>
                      {agent.endpoints.length > 0 && (
                        <div className="pt-3 border-t border-[var(--border-subtle)] mt-4">
                          <p className="text-xs font-mono text-[var(--text-muted)] mb-2">Known endpoints</p>
                          <div className="space-y-1">
                            {agent.endpoints.map((ep, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <span className={`px-1.5 py-0.5 text-[10px] font-mono font-medium rounded uppercase ${
                                  ep.protocol_type === 'mcp' ? 'bg-[var(--badge-mcp)]/20 text-[var(--badge-mcp)]'
                                  : ep.protocol_type === 'a2a' ? 'bg-[var(--badge-a2a)]/20 text-[var(--badge-a2a)]'
                                  : 'bg-[var(--text-muted)]/20 text-[var(--text-muted)]'
                                }`}>
                                  {ep.protocol_type}
                                </span>
                                <a href={ep.base_url} target="_blank" rel="noopener noreferrer" className="text-xs font-mono text-[var(--accent-teal)] hover:underline truncate">
                                  {ep.base_url}
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </motion.div>

              {/* Reviews List */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-[var(--bg-secondary)] rounded-2xl border border-[var(--border-default)] overflow-hidden"
              >
                <div className="px-6 py-4 border-b border-[var(--border-subtle)]">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-display font-medium text-[var(--text-primary)]">
                      Reviews ({selectedTag ? filteredFeedback.length : totalFeedback})
                    </h2>
                  </div>
                  {uniqueTags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      <button
                        onClick={() => setSelectedTag(null)}
                        className={`px-2.5 py-1 text-xs font-mono rounded-lg border transition-colors ${
                          selectedTag === null
                            ? 'bg-[var(--accent-teal)] text-[var(--bg-primary)] border-[var(--accent-teal)]'
                            : 'border-[var(--border-default)] text-[var(--text-muted)] hover:border-[var(--accent-teal)] hover:text-[var(--accent-teal)]'
                        }`}
                      >
                        All
                      </button>
                      {(showAllTags ? uniqueTags : uniqueTags.slice(0, TAGS_LIMIT)).map(tag => (
                        <button
                          key={tag}
                          onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                          className={`px-2.5 py-1 text-xs font-mono rounded-lg border transition-colors ${
                            selectedTag === tag
                              ? 'bg-[var(--accent-teal)] text-[var(--bg-primary)] border-[var(--accent-teal)]'
                              : 'border-[var(--border-default)] text-[var(--text-muted)] hover:border-[var(--accent-teal)] hover:text-[var(--accent-teal)]'
                          }`}
                        >
                          {tag}
                        </button>
                      ))}
                      {uniqueTags.length > TAGS_LIMIT && (
                        <button
                          onClick={() => setShowAllTags(v => !v)}
                          className="px-2.5 py-1 text-xs font-mono rounded-lg border border-[var(--border-default)] text-[var(--text-muted)] hover:border-[var(--accent-teal)] hover:text-[var(--accent-teal)] transition-colors"
                        >
                          {showAllTags ? 'Show less' : `+${uniqueTags.length - TAGS_LIMIT} more`}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="divide-y divide-[var(--border-subtle)]">
                  {loadingFeedback ? (
                    <div className="px-6 py-8 text-center text-[var(--text-muted)] font-mono text-sm">
                      Loading reviews...
                    </div>
                  ) : filteredFeedback.length === 0 ? (
                    <div className="px-6 py-8 text-center text-[var(--text-muted)] font-mono text-sm">
                      {selectedTag ? `No reviews with tag "${selectedTag}"` : 'No reviews yet. Be the first to leave feedback!'}
                    </div>
                  ) : (
                    (showAllReviews ? filteredFeedback : filteredFeedback.slice(0, 5)).map((item, i) => (
                      <div key={i} className="px-6 py-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <StarRating score={item.score / 20} />
                            <span className="px-2 py-0.5 text-sm font-mono font-semibold bg-[var(--accent-gold)]/15 text-[var(--accent-gold)] rounded">
                              {item.score}/100
                            </span>
                            {item.timestamp && (
                              <span className="text-xs text-[var(--text-muted)]">· {formatRelativeTime(item.timestamp)}</span>
                            )}
                          </div>
                          {(item.tag1 || item.tag2) && (
                            <div className="flex gap-1.5">
                              {item.tag1 && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-[var(--accent-teal)]/10 text-[var(--accent-teal)] rounded">
                                  {item.tag1}
                                </span>
                              )}
                              {item.tag2 && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-[var(--accent-teal)]/10 text-[var(--accent-teal)] rounded">
                                  {item.tag2}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        {item.comment && (
                          <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-2">
                            "{item.comment}"
                          </p>
                        )}
                        {item.response && (
                          <div className="ml-4 pl-3 border-l-2 border-[var(--accent-teal)]/30 mb-2">
                            {item.response.comment && (
                              <p className="text-sm text-[var(--text-secondary)] leading-relaxed italic">
                                "{item.response.comment}"
                              </p>
                            )}
                            <div className="flex items-center gap-2 text-xs font-mono text-[var(--text-muted)] mt-1">
                              <span>Response by: {explorerUrl && item.response.responder ? (
                                <a href={`${explorerUrl}/address/${item.response.responder}`} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--accent-teal)] hover:underline transition-colors">{truncateAddress(item.response.responder)}</a>
                              ) : truncateAddress(item.response.responder || '')}</span>
                              {item.response.timestamp && (
                                <>
                                  <span className="text-[var(--text-muted)]/50">·</span>
                                  <span>{formatRelativeTime(item.response.timestamp)}</span>
                                </>
                              )}
                              {item.response.transaction_url && (
                                <>
                                  <span className="text-[var(--text-muted)]/50">·</span>
                                  <a
                                    href={item.response.transaction_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[var(--accent-teal)] hover:underline"
                                  >
                                    View tx →
                                  </a>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                        <div className="flex items-center justify-between text-xs font-mono text-[var(--text-muted)]">
                          <span>By: {explorerUrl ? (
                            <a href={`${explorerUrl}/address/${item.client}`} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--accent-teal)] hover:underline transition-colors">{truncateAddress(item.client)}</a>
                          ) : truncateAddress(item.client)}</span>
                          {item.transaction_url && (
                            <a
                              href={item.transaction_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[var(--accent-teal)] hover:underline"
                            >
                              View tx →
                            </a>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                  {filteredFeedback.length > 5 && (
                    <div className="px-6 py-3 text-center">
                      <button
                        onClick={() => setShowAllReviews(!showAllReviews)}
                        className="text-sm font-mono text-[var(--accent-teal)] hover:underline"
                      >
                        {showAllReviews ? 'Show less' : `View all ${filteredFeedback.length} reviews`}
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-6">
              {/* Quick Actions */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-[var(--bg-secondary)] rounded-2xl border border-[var(--border-default)] p-5"
              >
                <div className="space-y-3">
                  {agent.metadata ? (
                    <Link
                      to={`/agents/${encodeURIComponent(agent.agent_key)}/metadata`}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[var(--accent-teal)] text-[var(--bg-primary)] font-mono font-medium hover:opacity-90 transition-opacity"
                    >
                      <ExternalLinkIcon className="w-4 h-4" />
                      View Metadata
                    </Link>
                  ) : (
                    <div className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-[var(--border-default)] text-[var(--text-muted)] font-mono font-medium cursor-not-allowed opacity-50">
                      <ExternalLinkIcon className="w-4 h-4" />
                      No Metadata
                    </div>
                  )}
                  {nftUrl && (
                    <a
                      href={nftUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-[var(--border-default)] text-[var(--text-secondary)] font-mono hover:bg-[var(--bg-tertiary)] transition-colors"
                    >
                      <ExternalLinkIcon className="w-4 h-4" />
                      View on {chainName === 'Ethereum' ? 'Etherscan' : 'Explorer'}
                    </a>
                  )}
                </div>
              </motion.div>

              {/* Agent Details */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-[var(--bg-secondary)] rounded-2xl border border-[var(--border-default)] overflow-hidden"
              >
                <div className="px-5 py-3 border-b border-[var(--border-subtle)]">
                  <h3 className="text-sm font-mono font-medium text-[var(--text-primary)]">Details</h3>
                </div>
                <div className="p-5 space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[var(--text-muted)]">Network</span>
                    <span className="font-mono text-[var(--text-primary)]">{chainName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--text-muted)]">Agent ID</span>
                    {nftUrl ? (
                      <a
                        href={nftUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-[var(--accent-teal)] hover:underline"
                      >
                        #{agent.token_id}
                      </a>
                    ) : (
                      <span className="font-mono text-[var(--text-primary)]">#{agent.token_id}</span>
                    )}
                  </div>
                  {agent.registered_at && (
                    <div className="flex justify-between">
                      <span className="text-[var(--text-muted)]">Registered</span>
                      <span className="font-mono text-[var(--text-primary)]">{formatDate(agent.registered_at)}</span>
                    </div>
                  )}
                  {agent.owner_address && (
                    <div className="flex justify-between">
                      <span className="text-[var(--text-muted)]">Owner</span>
                      {explorerUrl ? (
                        <a
                          href={`${explorerUrl}/address/${agent.owner_address}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-[var(--accent-teal)] hover:underline"
                        >
                          {truncateAddress(agent.owner_address)}
                        </a>
                      ) : (
                        <span className="font-mono text-[var(--accent-teal)]">{truncateAddress(agent.owner_address)}</span>
                      )}
                    </div>
                  )}
                  {agent.agent_wallet && (
                    <div className="flex justify-between">
                      <span className="text-[var(--text-muted)]">Agent Wallet</span>
                      {explorerUrl ? (
                        <a
                          href={`${explorerUrl}/address/${agent.agent_wallet}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-[var(--accent-teal)] hover:underline"
                        >
                          {truncateAddress(agent.agent_wallet)}
                        </a>
                      ) : (
                        <span className="font-mono text-[var(--accent-teal)]">{truncateAddress(agent.agent_wallet)}</span>
                      )}
                    </div>
                  )}
                  {agent.updated_at && (
                    <div className="flex justify-between">
                      <span className="text-[var(--text-muted)]">Last Updated</span>
                      <span className="font-mono text-[var(--text-primary)]">{formatRelativeTime(new Date(agent.updated_at).getTime())}</span>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* ========== MOBILE LAYOUT (< lg) ========== */}
      <div className="lg:hidden">
        {/* POI-Style Header Card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto px-4 pt-4"
        >
        <div className="bg-[var(--bg-secondary)] rounded-2xl border border-[var(--border-default)] overflow-hidden">
          {/* Hero Section */}
          <div className="p-5">
            {/* Avatar + Title Row */}
            <div className="flex gap-4 mb-4">
              {agent.image_url ? (
                <img
                  src={agent.image_url}
                  alt=""
                  className="w-16 h-16 rounded-xl object-cover bg-[var(--bg-tertiary)] flex-shrink-0"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[var(--accent-teal)]/20 to-[var(--accent-purple)]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-display font-bold text-[var(--accent-teal)]">
                    {(agent.name || '?')[0]?.toUpperCase()}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h1 className="font-display text-xl font-semibold text-[var(--text-primary)] truncate">
                  {agent.name || `Agent #${agent.token_id}`}
                </h1>

                {/* Rating Row */}
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-lg font-semibold text-[var(--text-primary)]">{avgScore.toFixed(1)}</span>
                  <Tooltip text="Score calculated using an internal algorithm">
                    <StarRating score={avgScore} />
                  </Tooltip>
                  <span className="text-sm text-[var(--text-muted)]">
                    ({totalFeedback} {totalFeedback === 1 ? 'review' : 'reviews'})
                  </span>
                  <span className="text-[var(--text-muted)]">·</span>
                  <span className="text-sm text-[var(--text-secondary)]">{chainName}</span>
                </div>
              </div>
            </div>

            {/* Description */}
            {agent.description && (
              <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4">
                <LinkifiedText text={agent.description} />
              </p>
            )}

            {/* Attribute Badges - Below Description (Mobile) */}
            <div className="flex items-center gap-2 flex-wrap mb-4">
              <Tooltip text={statusTooltip}>
                <span
                  className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-mono ${
                    isCallable
                      ? 'bg-[var(--status-online)]/15 text-[var(--status-online)]'
                      : isReachable
                      ? 'bg-[var(--accent-gold)]/15 text-[var(--accent-gold)]'
                      : 'bg-[var(--text-muted)]/15 text-[var(--text-muted)]'
                  }`}
                >
                  {statusLabel}
                </span>
              </Tooltip>
              {(agent.x402_support === 'true' ||
                agent.metadata?.x402Support === true ||
                (agent.metadata?.additional_fields as Record<string, unknown>)?.x402support === true ||
                (agent.metadata?.additional_fields as Record<string, unknown>)?.x402Support === true) && (
                <Tooltip text="Supports x402 payment protocol for paid API calls">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono bg-[var(--accent-gold)]/15 text-[var(--accent-gold)]">
                    <span>$</span> x402
                  </span>
                </Tooltip>
              )}
              {/* Domain Verification Badge (Mobile) */}
              {agent.domain_verified !== null && (
                <Tooltip text={verificationMessage}>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono ${
                    isAgentVerified
                      ? 'bg-[var(--status-online)]/15 text-[var(--status-online)]'
                      : 'bg-[var(--text-muted)]/15 text-[var(--text-muted)]'
                  }`}>
                    {isAgentVerified ? (
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    )}
                    {isAgentVerified ? 'Verified' : 'Unverified'}
                  </span>
                </Tooltip>
              )}
              {/* Reputation Trust Badge (Mobile) */}
              {(agent.metadata?.supportedTrust as string[] | undefined)?.includes('reputation') && (
                <Tooltip text="Agent supports on-chain reputation feedback">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono bg-[var(--accent-teal)]/15 text-[var(--accent-teal)]">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    Reputation
                  </span>
                </Tooltip>
              )}
            </div>

            {/* Capabilities - Highlighted */}
            {capabilities.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <SparklesIcon className="w-4 h-4 text-[var(--accent-purple)]" />
                  <span className="text-xs font-mono text-[var(--text-muted)] uppercase tracking-wide">Capabilities</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {capabilities.slice(0, 8).map((cap, i) => (
                    <span
                      key={i}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border break-words max-w-full ${
                        cap.type === 'skill'
                          ? 'bg-[var(--accent-purple)]/10 text-[var(--accent-purple)] border-[var(--accent-purple)]/20'
                          : cap.type === 'domain'
                          ? 'bg-[var(--accent-teal)]/10 text-[var(--accent-teal)] border-[var(--accent-teal)]/20'
                          : 'bg-[var(--accent-gold)]/10 text-[var(--accent-gold)] border-[var(--accent-gold)]/20'
                      }`}
                    >
                      {cap.protocol && (
                        <span className="text-[10px] opacity-60 uppercase">{cap.protocol === 'http_unknown' ? 'http' : cap.protocol}</span>
                      )}
                      {cap.name}
                    </span>
                  ))}
                  {capabilities.length > 8 && (
                    <span className="px-2.5 py-1 text-xs font-mono text-[var(--text-muted)]">
                      +{capabilities.length - 8} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="flex gap-2">
              {agent.metadata ? (
                <Link
                  to={`/agents/${encodeURIComponent(agent.agent_key)}/metadata`}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[var(--accent-teal)] text-[var(--bg-primary)] font-mono text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  <ExternalLinkIcon className="w-4 h-4" />
                  View Metadata
                </Link>
              ) : (
                <div className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-[var(--border-default)] text-[var(--text-muted)] font-mono text-sm font-medium cursor-not-allowed opacity-50">
                  <ExternalLinkIcon className="w-4 h-4" />
                  No Metadata
                </div>
              )}
              {nftUrl && (
                <a
                  href={nftUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-[var(--border-default)] text-[var(--text-secondary)] font-mono text-sm hover:bg-[var(--bg-tertiary)] transition-colors"
                >
                  <ExternalLinkIcon className="w-4 h-4" />
                  {chainName === 'Ethereum' ? 'Etherscan' : 'Explorer'}
                </a>
              )}
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex border-t border-[var(--border-subtle)]">
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex-1 px-4 py-3 text-sm font-mono transition-colors relative ${
                activeTab === 'overview'
                  ? 'text-[var(--accent-teal)]'
                  : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
              }`}
            >
              Overview
              {activeTab === 'overview' && (
                <motion.div layoutId="tab-indicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--accent-teal)]" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`flex-1 px-4 py-3 text-sm font-mono transition-colors relative ${
                activeTab === 'reviews'
                  ? 'text-[var(--accent-teal)]'
                  : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
              }`}
            >
              Reviews ({totalFeedback})
              {activeTab === 'reviews' && (
                <motion.div layoutId="tab-indicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--accent-teal)]" />
              )}
            </button>
          </div>
        </div>
      </motion.div>

      {/* Tab Content */}
      <div className="max-w-2xl mx-auto px-4 py-4">
        {activeTab === 'overview' ? (
          <motion.div
            key="overview"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            {/* Overview Card */}
            <section className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--border-default)] overflow-hidden">
              <div className="px-4 py-3 border-b border-[var(--border-subtle)]">
                <h3 className="text-sm font-mono font-medium text-[var(--text-primary)]">Overview</h3>
              </div>
              <div className="divide-y divide-[var(--border-subtle)]">
                <DetailRow
                  icon={<LayersIcon className="w-4 h-4" />}
                  label="Network"
                  value={chainName}
                />
                <DetailRow
                  icon={<HashIcon className="w-4 h-4" />}
                  label="Agent ID"
                  value={`#${agent.token_id}`}
                  href={nftUrl || undefined}
                />
                {agent.registered_at && (
                  <DetailRow
                    icon={<ClockIcon className="w-4 h-4" />}
                    label="Registered"
                    value={formatDate(agent.registered_at)}
                    subvalue={agent.registration_block ? `Block #${agent.registration_block.toLocaleString()}` : undefined}
                  />
                )}
                {agent.owner_address && (
                  <DetailRow
                    icon={<UserIcon className="w-4 h-4" />}
                    label="Owner"
                    value={truncateAddress(agent.owner_address)}
                    copyable={agent.owner_address}
                    href={explorerUrl ? `${explorerUrl}/address/${agent.owner_address}` : undefined}
                  />
                )}
                {agent.agent_wallet && (
                  <DetailRow
                    icon={<WalletIcon className="w-4 h-4" />}
                    label="Agent Wallet"
                    value={truncateAddress(agent.agent_wallet)}
                    copyable={agent.agent_wallet}
                    href={explorerUrl ? `${explorerUrl}/address/${agent.agent_wallet}` : undefined}
                  />
                )}
                {agent.updated_at && (
                  <DetailRow
                    icon={<ClockIcon className="w-4 h-4" />}
                    label="Last Updated"
                    value={formatRelativeTime(new Date(agent.updated_at).getTime()) || 'Unknown'}
                  />
                )}
              </div>
            </section>

            {/* Overview */}
            <section className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--border-default)] overflow-hidden">
              <div className="px-4 py-3 border-b border-[var(--border-subtle)]">
                <h3 className="text-sm font-mono font-medium text-[var(--text-primary)]">Overview</h3>
              </div>
              {agent.metadata?.services && agent.metadata.services.length > 0 ? (
                <div className="divide-y divide-[var(--border-subtle)]">
                  {agent.metadata.services.map((service, i) => {
                    const serviceName = service.name?.toUpperCase() || 'UNKNOWN';
                    const isMCP = serviceName === 'MCP';
                    const isA2A = serviceName === 'A2A';
                    return (
                      <div key={i} className="px-4 py-3">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 text-xs font-mono font-medium rounded uppercase ${
                              isMCP ? 'bg-[var(--badge-mcp)]/20 text-[var(--badge-mcp)]'
                              : isA2A ? 'bg-[var(--badge-a2a)]/20 text-[var(--badge-a2a)]'
                              : 'bg-[var(--text-muted)]/20 text-[var(--text-muted)]'
                            }`}>
                              {serviceName}
                            </span>
                            {/* Show verified checkmark for this specific endpoint */}
                            {service.endpoint && (agent.verified_endpoint === service.endpoint) && (
                              <Tooltip text="This endpoint domain is verified">
                                <span className="inline-flex items-center p-0.5 rounded bg-[var(--status-online)]/15 text-[var(--status-online)]">
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                </span>
                              </Tooltip>
                            )}
                          </div>
                          {isMCP && service.endpoint && (() => {
                            const info = mcpEndpointInfo[service.endpoint!]
                            const hasTools = info?.toolCount && info.toolCount > 0
                            const tooltipMessage = !info?.loaded
                              ? 'Loading tools...'
                              : !hasTools
                              ? 'No tools found at endpoint'
                              : null

                            const button = (
                              <button
                                onClick={() => {
                                  if (hasTools) {
                                    setTestingEndpoint({ url: service.endpoint!, protocol: 'mcp' })
                                  }
                                }}
                                disabled={!hasTools}
                                className={`px-2 py-1 rounded-lg border font-mono text-xs font-medium transition-colors ${
                                  hasTools
                                    ? 'border-[var(--accent-purple)] text-[var(--accent-purple)] hover:bg-[var(--accent-purple)]/10'
                                    : 'border-[var(--border-default)] text-[var(--text-muted)] cursor-not-allowed opacity-50'
                                }`}
                              >
                                Try Tools
                              </button>
                            )

                            return tooltipMessage ? (
                              <Tooltip text={tooltipMessage}>{button}</Tooltip>
                            ) : button
                          })()}
                        </div>
                        {service.endpoint && (
                          <a
                            href={service.endpoint}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-mono text-[var(--accent-teal)] hover:underline break-all"
                          >
                            {service.endpoint}
                          </a>
                        )}
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="px-4 py-6 text-center">
                  <p className="text-xs font-mono text-[var(--text-muted)]">No metadata registered</p>
                  <p className="text-xs text-[var(--text-muted)]/70 mt-1">Services will appear here once metadata is published.</p>
                  {agent.endpoints.length > 0 && (
                    <div className="pt-3 border-t border-[var(--border-subtle)] mt-3 text-left">
                      <p className="text-[10px] font-mono text-[var(--text-muted)] mb-1.5">Known endpoints</p>
                      <div className="space-y-1">
                        {agent.endpoints.map((ep, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className={`px-1.5 py-0.5 text-[10px] font-mono font-medium rounded uppercase ${
                              ep.protocol_type === 'mcp' ? 'bg-[var(--badge-mcp)]/20 text-[var(--badge-mcp)]'
                              : ep.protocol_type === 'a2a' ? 'bg-[var(--badge-a2a)]/20 text-[var(--badge-a2a)]'
                              : 'bg-[var(--text-muted)]/20 text-[var(--text-muted)]'
                            }`}>
                              {ep.protocol_type}
                            </span>
                            <a href={ep.base_url} target="_blank" rel="noopener noreferrer" className="text-xs font-mono text-[var(--accent-teal)] hover:underline truncate">
                              {ep.base_url}
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* All Capabilities (full list) */}
            {capabilities.length > 8 && (
              <section className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--border-default)] overflow-hidden">
                <div className="px-4 py-3 border-b border-[var(--border-subtle)]">
                  <h3 className="text-sm font-mono font-medium text-[var(--text-primary)]">All Capabilities ({capabilities.length})</h3>
                </div>
                <div className="p-4">
                  <div className="flex flex-wrap gap-1.5">
                    {capabilities.map((cap, i) => (
                      <span
                        key={i}
                        className={`px-2 py-0.5 text-xs font-mono rounded ${
                          cap.type === 'skill'
                            ? 'bg-[var(--accent-purple)]/15 text-[var(--accent-purple)]'
                            : cap.type === 'domain'
                            ? 'bg-[var(--accent-teal)]/15 text-[var(--accent-teal)]'
                            : 'bg-[var(--accent-gold)]/15 text-[var(--accent-gold)]'
                        }`}
                      >
                        {cap.name}
                      </span>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Metadata */}
            {agent.metadata && Object.keys(agent.metadata).length > 0 && (
              <section className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--border-default)] overflow-hidden">
                <div className="px-4 py-3 border-b border-[var(--border-subtle)]">
                  <h3 className="text-sm font-mono font-medium text-[var(--text-primary)]">Metadata</h3>
                </div>
                <div className="p-4">
                  <MetadataView metadata={agent.metadata} />
                </div>
              </section>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="reviews"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-3"
          >
            {/* Tag Filters */}
            {uniqueTags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pb-2">
                <button
                  onClick={() => setSelectedTag(null)}
                  className={`px-2.5 py-1 text-xs font-mono rounded-lg border transition-colors ${
                    selectedTag === null
                      ? 'bg-[var(--accent-teal)] text-[var(--bg-primary)] border-[var(--accent-teal)]'
                      : 'border-[var(--border-default)] text-[var(--text-muted)] hover:border-[var(--accent-teal)] hover:text-[var(--accent-teal)]'
                  }`}
                >
                  All
                </button>
                {(showAllTags ? uniqueTags : uniqueTags.slice(0, TAGS_LIMIT)).map(tag => (
                  <button
                    key={tag}
                    onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                    className={`px-2.5 py-1 text-xs font-mono rounded-lg border transition-colors ${
                      selectedTag === tag
                        ? 'bg-[var(--accent-teal)] text-[var(--bg-primary)] border-[var(--accent-teal)]'
                        : 'border-[var(--border-default)] text-[var(--text-muted)] hover:border-[var(--accent-teal)] hover:text-[var(--accent-teal)]'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
                {uniqueTags.length > TAGS_LIMIT && (
                  <button
                    onClick={() => setShowAllTags(v => !v)}
                    className="px-2.5 py-1 text-xs font-mono rounded-lg border border-[var(--border-default)] text-[var(--text-muted)] hover:border-[var(--accent-teal)] hover:text-[var(--accent-teal)] transition-colors"
                  >
                    {showAllTags ? 'Show less' : `+${uniqueTags.length - TAGS_LIMIT} more`}
                  </button>
                )}
              </div>
            )}
            {loadingFeedback ? (
              <div className="text-center py-8 text-[var(--text-muted)] font-mono text-sm">
                Loading reviews...
              </div>
            ) : filteredFeedback.length === 0 ? (
              <div className="text-center py-8 text-[var(--text-muted)] font-mono text-sm">
                {selectedTag ? `No reviews with tag "${selectedTag}"` : 'No reviews yet'}
              </div>
            ) : (
              filteredFeedback.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--border-default)] p-4"
                >
                  {/* Score and Tags */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <StarRating score={item.score / 20} />
                      <span className="px-2 py-0.5 text-sm font-mono font-semibold bg-[var(--accent-gold)]/15 text-[var(--accent-gold)] rounded">
                        {item.score}/100
                      </span>
                    </div>
                    {(item.tag1 || item.tag2) && (
                      <div className="flex gap-1.5">
                        {item.tag1 && (
                          <span className="px-2.5 py-1 text-xs font-medium bg-[var(--accent-teal)]/10 text-[var(--accent-teal)] rounded-lg border border-[var(--accent-teal)]/20">
                            {item.tag1}
                          </span>
                        )}
                        {item.tag2 && (
                          <span className="px-2.5 py-1 text-xs font-medium bg-[var(--accent-teal)]/10 text-[var(--accent-teal)] rounded-lg border border-[var(--accent-teal)]/20">
                            {item.tag2}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Comment text if present */}
                  {item.comment && (
                    <p className="text-sm text-[var(--text-secondary)] mt-2 italic leading-relaxed">
                      "{item.comment}"
                    </p>
                  )}

                  {/* Agent response if present */}
                  {item.response && (
                    <div className="ml-4 pl-3 mt-2 border-l-2 border-[var(--accent-teal)]/30">
                      {item.response.comment && (
                        <p className="text-sm text-[var(--text-secondary)] leading-relaxed italic">
                          "{item.response.comment}"
                        </p>
                      )}
                      <div className="flex items-center gap-2 text-xs font-mono text-[var(--text-muted)] mt-1">
                        <span>Response by: {explorerUrl && item.response.responder ? (
                          <a href={`${explorerUrl}/address/${item.response.responder}`} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--accent-teal)] hover:underline transition-colors">{truncateAddress(item.response.responder)}</a>
                        ) : truncateAddress(item.response.responder || '')}</span>
                        {item.response.timestamp && (
                          <>
                            <span className="text-[var(--text-muted)]/50">·</span>
                            <span>{formatRelativeTime(item.response.timestamp)}</span>
                          </>
                        )}
                        {item.response.transaction_url && (
                          <>
                            <span className="text-[var(--text-muted)]/50">·</span>
                            <a
                              href={item.response.transaction_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-[var(--accent-teal)] hover:underline"
                            >
                              View tx
                              <ExternalLinkIcon className="w-3 h-3" />
                            </a>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Client, Timestamp, and Transaction Link */}
                  <div className="flex items-center justify-between text-xs font-mono text-[var(--text-muted)] mt-3">
                    <div className="flex items-center gap-2">
                      <span>By: {explorerUrl ? (
                        <a href={`${explorerUrl}/address/${item.client}`} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--accent-teal)] hover:underline transition-colors">{truncateAddress(item.client)}</a>
                      ) : truncateAddress(item.client)}</span>
                      {item.timestamp && (
                        <>
                          <span className="text-[var(--text-muted)]/50">•</span>
                          <span>{formatRelativeTime(item.timestamp)}</span>
                        </>
                      )}
                    </div>
                    {item.transaction_url && (
                      <a
                        href={item.transaction_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-[var(--accent-teal)] hover:underline"
                      >
                        View tx
                        <ExternalLinkIcon className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </motion.div>
              ))
            )}
          </motion.div>
        )}
      </div>
      </div>

      {/* Tool Tester Modal */}
      {testingEndpoint && (
        <ToolTester
          endpointUrl={testingEndpoint.url}
          protocol={testingEndpoint.protocol}
          initialToolName={testingEndpoint.toolName}
          onClose={() => setTestingEndpoint(null)}
          paymentStatus={Object.fromEntries(
            liveCapabilities
              .filter(c => c.protocol === 'mcp' && c.endpointUrl === testingEndpoint.url)
              .map(c => [c.name, c.requiresPayment ?? null])
          )}
        />
      )}

      {/* A2A Messenger Modal */}
      {a2aMessenger && (
        <A2AMessenger
          endpointUrl={a2aMessenger.url}
          skillName={a2aMessenger.skillName}
          transport={a2aMessenger.transport}
          warning={a2aMessenger.warning}
          examples={a2aMessenger.examples}
          requiresPayment={a2aMessenger.requiresPayment}
          paymentInfo={a2aMessenger.paymentInfo}
          onClose={() => setA2aMessenger(null)}
        />
      )}

      {/* Capability Detail Modal */}
      {selectedCapability && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setSelectedCapability(null)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-lg max-h-[80vh] rounded-2xl border shadow-2xl overflow-hidden flex flex-col ${
              selectedCapability.type === 'skill'
                ? 'bg-[var(--bg-secondary)] border-[var(--accent-purple)]/30'
                : selectedCapability.type === 'domain'
                ? 'bg-[var(--bg-secondary)] border-[var(--accent-teal)]/30'
                : 'bg-[var(--bg-secondary)] border-[var(--accent-gold)]/30'
            }`}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-[var(--border-subtle)] flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className={`text-lg font-mono font-medium break-words ${
                  selectedCapability.type === 'skill' ? 'text-[var(--accent-purple)]'
                  : selectedCapability.type === 'domain' ? 'text-[var(--accent-teal)]'
                  : 'text-[var(--accent-gold)]'
                }`}>
                  {toDisplayName(selectedCapability.name)}
                </h3>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {/* Show all source protocols (MCP, A2A, OASF) */}
                  {(() => {
                    const sources = selectedCapability.sources || [selectedCapability.source]
                    const protocols: string[] = []
                    if (sources.some(s => s.includes('MCP'))) protocols.push('MCP')
                    if (sources.some(s => s.includes('A2A'))) protocols.push('A2A')
                    if (sources.some(s => s.includes('OASF') || selectedCapability.protocol === 'oasf')) protocols.push('OASF')
                    return protocols.map(protocol => (
                      <span key={protocol} className="px-2 py-0.5 text-xs font-mono uppercase rounded bg-[var(--bg-tertiary)] text-[var(--text-muted)]">
                        {protocol}
                      </span>
                    ))
                  })()}
                  {selectedCapability.isProbed && (
                    <span className="px-2 py-0.5 text-xs font-mono rounded bg-[var(--status-online)]/20 text-[var(--status-online)]">
                      live
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setSelectedCapability(null)}
                className="p-2 rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors flex-shrink-0"
              >
                <XIcon className="w-5 h-5 text-[var(--text-muted)]" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6 space-y-4">
              {selectedCapability.description && (
                <div>
                  <div className="text-xs font-mono text-[var(--text-muted)] mb-1">Description</div>
                  <p className="text-sm text-[var(--text-secondary)]">{selectedCapability.description}</p>
                </div>
              )}

              <div>
                <div className="text-xs font-mono text-[var(--text-muted)] mb-1">
                  {selectedCapability.sources && selectedCapability.sources.length > 1 ? 'Sources (merged)' : 'Source'}
                </div>
                {selectedCapability.sources && selectedCapability.sources.length > 1 ? (
                  <ul className="text-sm font-mono text-[var(--text-secondary)] space-y-1">
                    {selectedCapability.sources.map((src, i) => {
                      // Extract URL from source string (e.g., "MCP endpoint (live): https://...")
                      const urlMatch = src.match(/(https?:\/\/[^\s]+)/)
                      const url = urlMatch ? urlMatch[1] : null
                      const prefix = url ? src.replace(url, '').trim() : src
                      return (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-[var(--accent-teal)]">•</span>
                          <span className="break-all">
                            {prefix}{' '}
                            {url && (
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[var(--accent-teal)] hover:underline"
                              >
                                {url}
                              </a>
                            )}
                          </span>
                        </li>
                      )
                    })}
                  </ul>
                ) : (
                  <p className="text-sm font-mono text-[var(--text-secondary)] break-all">
                    {(() => {
                      const src = selectedCapability.source
                      const urlMatch = src.match(/(https?:\/\/[^\s]+)/)
                      const url = urlMatch ? urlMatch[1] : null
                      const prefix = url ? src.replace(url, '').trim() : src
                      return (
                        <>
                          {prefix}{' '}
                          {url && (
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[var(--accent-teal)] hover:underline"
                            >
                              {url}
                            </a>
                          )}
                        </>
                      )
                    })()}
                  </p>
                )}
              </div>

              {/* Show all raw JSON if merged, otherwise single */}
              {selectedCapability.allRawJson && selectedCapability.allRawJson.length > 0 ? (
                <div className="space-y-4">
                  {selectedCapability.allRawJson.map((item, i) => {
                    const isMCP = item.source.includes('MCP')
                    const isA2A = item.source.includes('A2A')
                    const isOASF = item.source.includes('OASF') || item.source.includes('oasf') || item.source.includes('(metadata)')
                    return (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-xs font-mono text-[var(--text-muted)]">
                            {isMCP ? 'MCP Tool' : isA2A ? 'A2A Skill' : isOASF ? 'OASF Metadata' : 'Raw JSON'}
                          </div>
                          {isMCP && (selectedCapability.endpointsByProtocol?.mcp || selectedCapability.endpointUrl) && (
                            <button
                              onClick={() => {
                                const mcpUrl = selectedCapability.endpointsByProtocol?.mcp || selectedCapability.endpointUrl!
                                // Use the actual tool name from rawJson (not the display name)
                                const actualToolName = (selectedCapability.rawJson?.name as string) || selectedCapability.name
                                setTestingEndpoint({ url: mcpUrl, protocol: 'mcp', toolName: actualToolName })
                                setSelectedCapability(null)
                              }}
                              className="px-3 py-1 rounded-lg border border-[var(--accent-teal)] text-[var(--accent-teal)] text-xs font-mono font-medium hover:bg-[var(--accent-teal)]/10 transition-colors"
                            >
                              Try Tool
                            </button>
                          )}
                          {isA2A && (() => {
                            // Look up messaging info using the A2A-specific endpoint URL
                            const a2aUrl = selectedCapability.endpointsByProtocol?.a2a || selectedCapability.endpointUrl
                            const info = a2aUrl ? a2aEndpointInfo[a2aUrl] : undefined
                            const messagingUrl = info?.messagingUrl || selectedCapability.messagingUrl
                            const warning = info?.warning || selectedCapability.messagingUrlWarning
                            const transport = info?.transport || selectedCapability.preferredTransport
                            const requiresPayment = info?.requiresPayment ?? selectedCapability.requiresPayment

                            const tooltipText = !messagingUrl
                              ? 'No "url" field in A2A schema'
                              : warning || null

                            const button = (
                              <button
                                onClick={() => {
                                  if (messagingUrl) {
                                    setA2aMessenger({ url: messagingUrl, skillName: selectedCapability.name, transport, warning, examples: selectedCapability.examples, requiresPayment, paymentInfo: info?.paymentInfo })
                                    setSelectedCapability(null)
                                  }
                                }}
                                disabled={!messagingUrl}
                                className={`px-3 py-1 rounded-lg border text-xs font-mono font-medium transition-colors flex items-center gap-1 ${
                                  messagingUrl
                                    ? 'border-[var(--accent-teal)] text-[var(--accent-teal)] hover:bg-[var(--accent-teal)]/10'
                                    : 'border-[var(--border-default)] text-[var(--text-muted)] cursor-not-allowed opacity-50'
                                }`}
                              >
                                {warning && (
                                  <svg className="w-3.5 h-3.5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                )}
                                {messagingUrl && requiresPayment === true && (
                                  <span className="text-amber-400 font-bold">$</span>
                                )}
                                {messagingUrl && requiresPayment === false && (
                                  <span className="text-emerald-400 text-[10px]">FREE</span>
                                )}
                                Send Message
                              </button>
                            )

                            return tooltipText ? (
                              <Tooltip text={tooltipText}>
                                {button}
                              </Tooltip>
                            ) : button
                          })()}
                        </div>
                        <pre className="p-4 rounded-xl bg-[var(--bg-tertiary)] text-xs font-mono text-[var(--text-secondary)] overflow-auto max-h-48 whitespace-pre-wrap break-all">
{JSON.stringify(item.data, null, 2)}
                        </pre>
                      </div>
                    )
                  })}
                </div>
              ) : selectedCapability.rawJson && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-mono text-[var(--text-muted)]">
                      {selectedCapability.protocol === 'mcp' ? 'MCP Tool'
                        : selectedCapability.protocol === 'a2a' ? 'A2A Skill'
                        : selectedCapability.protocol === 'oasf' ? 'OASF Metadata'
                        : 'Raw JSON'}
                    </div>
                    {selectedCapability.protocol === 'mcp' && (selectedCapability.endpointsByProtocol?.mcp || selectedCapability.endpointUrl) && (
                      <button
                        onClick={() => {
                          const mcpUrl = selectedCapability.endpointsByProtocol?.mcp || selectedCapability.endpointUrl!
                          // Use the actual tool name from rawJson (not the display name)
                          const actualToolName = (selectedCapability.rawJson?.name as string) || selectedCapability.name
                          setTestingEndpoint({ url: mcpUrl, protocol: 'mcp', toolName: actualToolName })
                          setSelectedCapability(null)
                        }}
                        className="px-3 py-1 rounded-lg bg-[var(--accent-purple)] text-white text-xs font-mono font-medium hover:opacity-90 transition-opacity"
                      >
                        Try Tool
                      </button>
                    )}
                    {selectedCapability.protocol === 'a2a' && (() => {
                      // Look up messaging info using the A2A-specific endpoint URL
                      const a2aUrl = selectedCapability.endpointsByProtocol?.a2a || selectedCapability.endpointUrl
                      const info = a2aUrl ? a2aEndpointInfo[a2aUrl] : undefined
                      const messagingUrl = info?.messagingUrl || selectedCapability.messagingUrl
                      const warning = info?.warning || selectedCapability.messagingUrlWarning
                      const transport = info?.transport || selectedCapability.preferredTransport
                      const requiresPayment = info?.requiresPayment ?? selectedCapability.requiresPayment

                      const tooltipText = !messagingUrl
                        ? 'No "url" field in A2A schema'
                        : warning || null

                      const button = (
                        <button
                          onClick={() => {
                            if (messagingUrl) {
                              setA2aMessenger({ url: messagingUrl, skillName: selectedCapability.name, transport, warning, examples: selectedCapability.examples, requiresPayment, paymentInfo: info?.paymentInfo })
                              setSelectedCapability(null)
                            }
                          }}
                          disabled={!messagingUrl}
                          className={`px-3 py-1 rounded-lg text-xs font-mono font-medium transition-opacity flex items-center gap-1 ${
                            messagingUrl
                              ? 'bg-[var(--accent-teal)] text-white hover:opacity-90'
                              : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] cursor-not-allowed opacity-50'
                          }`}
                        >
                          {warning && (
                            <svg className="w-3.5 h-3.5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          )}
                          {messagingUrl && requiresPayment === true && (
                            <span className="text-amber-400 font-bold">$</span>
                          )}
                          {messagingUrl && requiresPayment === false && (
                            <span className="text-emerald-400 text-[10px]">FREE</span>
                          )}
                          Send Message
                        </button>
                      )

                      return tooltipText ? (
                        <Tooltip text={tooltipText}>
                          {button}
                        </Tooltip>
                      ) : button
                    })()}
                  </div>
                  <pre className="p-4 rounded-xl bg-[var(--bg-tertiary)] text-xs font-mono text-[var(--text-secondary)] overflow-auto max-h-64 whitespace-pre-wrap break-all">
{JSON.stringify(selectedCapability.rawJson, null, 2)}
                  </pre>
                </div>
              )}
            </div>

          </motion.div>
        </motion.div>
      )}
    </div>
  )
}

function DetailRow({
  icon,
  label,
  value,
  subvalue,
  copyable,
  href
}: {
  icon: React.ReactNode
  label: string
  value: string
  subvalue?: string
  copyable?: string
  href?: string
}) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    if (!copyable) return
    navigator.clipboard.writeText(copyable)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="px-4 py-3 flex items-center gap-3">
      <span className="text-[var(--text-muted)]">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-[var(--text-muted)]">{label}</div>
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-mono text-[var(--accent-teal)] hover:underline truncate block"
          >
            {value}
          </a>
        ) : (
          <div className="text-sm font-mono text-[var(--text-primary)] truncate">{value}</div>
        )}
        {subvalue && <div className="text-xs font-mono text-[var(--text-muted)]">{subvalue}</div>}
      </div>
      {copyable && (
        <button
          onClick={handleCopy}
          className="p-1.5 rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors"
        >
          {copied ? (
            <CheckIcon className="w-4 h-4 text-[var(--status-online)]" />
          ) : (
            <CopyIcon className="w-4 h-4 text-[var(--text-muted)]" />
          )}
        </button>
      )}
    </div>
  )
}

function MetadataView({ metadata }: { metadata: AgentMetadata }) {
  // Filter out fields we already display elsewhere
  const excludeFields = ['name', 'description', 'image', 'services']
  const displayFields = Object.entries(metadata).filter(
    ([key]) => !excludeFields.includes(key) && metadata[key] !== null && metadata[key] !== undefined
  )

  if (displayFields.length === 0 && !metadata.services?.length) {
    return <div className="text-sm text-[var(--text-muted)] font-mono">No additional metadata</div>
  }

  return (
    <div className="space-y-3">
      {/* Services */}
      {metadata.services && metadata.services.length > 0 && (
        <div>
          <div className="text-xs text-[var(--text-muted)] mb-2">Services</div>
          <div className="space-y-2">
            {metadata.services.map((service, i) => (
              <div key={i} className="p-2 rounded-lg bg-[var(--bg-tertiary)]">
                <div className="text-sm font-mono text-[var(--text-primary)]">{service.name || 'Unnamed'}</div>
                {service.skills && service.skills.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {service.skills.slice(0, 5).map((skill, j) => (
                      <span key={j} className="px-1.5 py-0.5 text-xs font-mono bg-[var(--accent-purple)]/15 text-[var(--accent-purple)] rounded">
                        {skill}
                      </span>
                    ))}
                    {service.skills.length > 5 && (
                      <span className="text-xs text-[var(--text-muted)]">+{service.skills.length - 5}</span>
                    )}
                  </div>
                )}
                {service.domains && service.domains.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {service.domains.slice(0, 5).map((domain, j) => (
                      <span key={j} className="px-1.5 py-0.5 text-xs font-mono bg-[var(--accent-teal)]/15 text-[var(--accent-teal)] rounded">
                        {domain}
                      </span>
                    ))}
                    {service.domains.length > 5 && (
                      <span className="text-xs text-[var(--text-muted)]">+{service.domains.length - 5}</span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Other fields */}
      {displayFields.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {displayFields.map(([key, value]) => (
            <div key={key} className="p-2 rounded-lg bg-[var(--bg-tertiary)]">
              <div className="text-xs text-[var(--text-muted)]">{formatKey(key)}</div>
              <div className="text-sm font-mono text-[var(--text-primary)] truncate">
                {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function formatKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/^\w/, c => c.toUpperCase())
    .trim()
}

function formatDate(dateStr: string): string {
  try {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return 'Unknown'
  }
}

function truncateAddress(address: string): string {
  return `${address.slice(0, 6)}...${address.slice(-4)}`
}

const NETWORK_NAMES: Record<string, string> = {
  'eip155:1': 'Ethereum',
  'eip155:8453': 'Base',
  'eip155:84532': 'Base Sepolia',
  'eip155:11155111': 'Sepolia',
  'eip155:137': 'Polygon',
}

// Known stablecoin addresses (lowercase) → { name, decimals }
const KNOWN_ASSETS: Record<string, { name: string; decimals: number }> = {
  '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913': { name: 'USDC', decimals: 6 },  // USDC on Base
  '0x036cbd53842c5426634e7929541ec2318f3dcf7e': { name: 'USDC', decimals: 6 },  // USDC on Base Sepolia
  '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48': { name: 'USDC', decimals: 6 },  // USDC on Ethereum
  '0xdac17f958d2ee523a2206206994597c13d831ec7': { name: 'USDT', decimals: 6 },  // USDT on Ethereum
}

/** Extract a human-readable pricing summary from x402 payment info */
function formatPaymentInfo(info: Record<string, unknown> | undefined): string | null {
  if (!info) return null
  try {
    // x402 standard: { accepts: [{ scheme, network, amount, asset, payTo, extra }] }
    const accepts = info['accepts'] as Array<Record<string, unknown>> | undefined
    if (accepts && Array.isArray(accepts) && accepts.length > 0) {
      // Show all accepted payment options
      const options = accepts.map(offer => {
        const amount = offer['amount'] as string | undefined
        const network = offer['network'] as string | undefined
        const asset = (offer['asset'] as string || '').toLowerCase()
        const extra = offer['extra'] as Record<string, unknown> | undefined
        const tokenName = extra?.['name'] as string || KNOWN_ASSETS[asset]?.name || 'tokens'
        const decimals = KNOWN_ASSETS[asset]?.decimals ?? 18
        const networkName = network ? (NETWORK_NAMES[network] || network) : ''

        if (amount) {
          const formatted = (Number(amount) / Math.pow(10, decimals)).toString()
          return `${formatted} ${tokenName}${networkName ? ` on ${networkName}` : ''}`
        }
        return networkName ? `${tokenName} on ${networkName}` : tokenName
      })
      // Deduplicate if amounts are the same across networks
      return options.length === 1 ? options[0] : options.join(' or ')
    }

    // Resource description from x402 header
    const resource = info['resource'] as Record<string, unknown> | undefined
    if (resource?.['description']) return resource['description'] as string

    // Agent card x402 field fallback: { price, currency, network }
    const x402 = info['x402'] as Record<string, unknown> | undefined
    if (x402) {
      const price = x402['price'] as string | undefined
      const currency = x402['currency'] as string | undefined
      const network = x402['network'] as string | undefined
      if (price && currency) {
        const networkName = network ? (NETWORK_NAMES[network] || network) : ''
        return `${price} ${currency}${networkName ? ` on ${networkName}` : ''}`
      }
    }

    // Agent card authentication description fallback
    const auth = info['authentication'] as Record<string, unknown> | undefined
    if (auth?.['description']) return auth['description'] as string

    return null
  } catch {
    return null
  }
}

function Tooltip({ text, children, position = 'top' }: { text: string; children: React.ReactNode; position?: 'top' | 'bottom' }) {
  const [show, setShow] = useState(false)
  const [coords, setCoords] = useState({ x: 0, y: 0 })
  const triggerRef = useRef<HTMLDivElement>(null)

  const updatePosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setCoords({
        x: rect.left + rect.width / 2,
        y: position === 'top' ? rect.top - 8 : rect.bottom + 8
      })
    }
  }

  return (
    <div
      ref={triggerRef}
      className="relative inline-flex"
      onMouseEnter={() => { updatePosition(); setShow(true) }}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {createPortal(
        <div
          className={`
            fixed z-[9999] pointer-events-none
            transition-all duration-200 ease-out
            ${show ? 'opacity-100' : 'opacity-0'}
          `}
          style={{
            left: coords.x,
            top: coords.y,
            transform: `translate(-50%, ${position === 'top' ? '-100%' : '0%'})`
          }}
        >
          {/* Tooltip content */}
          <div className="relative px-3 py-2 rounded-lg whitespace-nowrap
            bg-[#0d1117]/95 backdrop-blur-md
            border border-[var(--accent-teal)]/30
            shadow-[0_0_20px_rgba(20,184,166,0.15),0_4px_12px_rgba(0,0,0,0.4)]
          ">
            {/* Subtle glow line at top */}
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[var(--accent-teal)]/50 to-transparent" />

            {/* Text */}
            <span className="text-xs font-mono text-[var(--text-secondary)] tracking-wide">
              {text}
            </span>

            {/* Arrow */}
            <div className={`
              absolute left-1/2 -translate-x-1/2
              ${position === 'top' ? 'top-full -mt-px' : 'bottom-full -mb-px rotate-180'}
            `}>
              <div className="relative">
                {/* Arrow border */}
                <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-[var(--accent-teal)]/30" />
                {/* Arrow fill */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[5px] border-t-[#0d1117]/95" />
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

function LinkifiedText({ text }: { text: string }) {
  // Match URLs in text
  const urlRegex = /(https?:\/\/[^\s<>)"']+)/g
  const parts = text.split(urlRegex)

  return (
    <>
      {parts.map((part, i) => {
        if (urlRegex.test(part)) {
          // Reset regex lastIndex since we're reusing it
          urlRegex.lastIndex = 0
          return (
            <a
              key={i}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--accent-teal)] hover:underline"
            >
              {part}
            </a>
          )
        }
        return <span key={i}>{part}</span>
      })}
    </>
  )
}

function StarRating({ score }: { score: number }) {
  const maxStars = 5
  const fullStars = Math.floor(score)
  const hasHalfStar = score - fullStars >= 0.5
  const emptyStars = maxStars - fullStars - (hasHalfStar ? 1 : 0)

  return (
    <div className="flex gap-0.5">
      {Array.from({ length: fullStars }).map((_, i) => (
        <StarIcon key={`full-${i}`} className="w-3.5 h-3.5 text-[var(--accent-gold)]" filled />
      ))}
      {hasHalfStar && <StarIcon className="w-3.5 h-3.5 text-[var(--accent-gold)]" half />}
      {Array.from({ length: emptyStars }).map((_, i) => (
        <StarIcon key={`empty-${i}`} className="w-3.5 h-3.5 text-[var(--text-muted)]" />
      ))}
    </div>
  )
}

// Icons
function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  )
}

function StarIcon({ className, filled, half }: { className?: string; filled?: boolean; half?: boolean }) {
  if (half) {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <defs>
          <linearGradient id="halfStar">
            <stop offset="50%" stopColor="currentColor" />
            <stop offset="50%" stopColor="transparent" />
          </linearGradient>
        </defs>
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="url(#halfStar)" strokeWidth={2} />
      </svg>
    )
  }
  return (
    <svg className={className} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2}>
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  )
}

function ExternalLinkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
    </svg>
  )
}

function CopyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  )
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  )
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function LayersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  )
}

function HashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
    </svg>
  )
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  )
}

function WalletIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a2.25 2.25 0 00-2.25-2.25H15a3 3 0 110-6h5.25A2.25 2.25 0 0121 6v6zm0 0v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18V6a2.25 2.25 0 012.25-2.25h13.5" />
    </svg>
  )
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  )
}

function SunIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  )
}

function MoonIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
    </svg>
  )
}

function LoadingDots() {
  return (
    <span className="inline-flex gap-0.5">
      <span className="w-1 h-1 bg-current rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
      <span className="w-1 h-1 bg-current rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
      <span className="w-1 h-1 bg-current rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
    </span>
  )
}

